import React from 'react';
import { WrenchScrewdriverIcon, SparklesIcon } from '@heroicons/react/24/outline';
import PlaybookSection from '../../../components/playbooks/PlaybookSection';
import PlaybookCard from '../../../components/playbooks/PlaybookCard';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';
import ToolCallout from '../../../components/playbooks/ToolCallout';
import CopyToNotion from '../../../components/playbooks/CopyToNotion';
import PlaybookImage from '../../../components/playbooks/PlaybookImage';
import VideoEmbed from '../../../components/playbooks/VideoEmbed';

const Stage2 = () => {
  return (
    <PlaybookSection
      title="Stage 2: Building Your First AI Products"
      icon={<WrenchScrewdriverIcon className="h-7 w-7 text-primary-light" />}
      heading="Ship Something Real"
    >
      <div className="space-y-8">
        {/* TL;DR */}
        <ExpandableCallout 
          title="TL;DR - What you'll accomplish in this stage"
          type="info"
          defaultOpen={true}
        >
          <ul className="space-y-2 text-gray-300">
            <li className="flex items-start">
              <span className="text-primary-light font-bold mr-3">1.</span>
              <span>Learn the fundamentals of prompt engineering for GPT agents</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light font-bold mr-3">2.</span>
              <span>Build your first custom GPT using our proven template</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light font-bold mr-3">3.</span>
              <span>Create a simple UI wrapper using Bolt.new or Lovable</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light font-bold mr-3">4.</span>
              <span>Deploy and test your first AI tool with real users</span>
            </li>
          </ul>
        </ExpandableCallout>

        {/* What do I need to DO */}
        <ExpandableCallout 
          title="What do I need to DO in this stage?"
          type="tip"
          defaultOpen={true}
        >
          <ol className="space-y-3 text-gray-300">
            <li className="flex items-start">
              <span className="text-primary-light font-bold mr-3">1.</span>
              <span>Pick one high-impact use case from Stage 1 to prototype</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light font-bold mr-3">2.</span>
              <span>Write a strong prompt using our Agent Builder template</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light font-bold mr-3">3.</span>
              <span>Test the prompt with real, messy input data</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light font-bold mr-3">4.</span>
              <span>Create a simple UI using Bolt.new or Lovable.com</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light font-bold mr-3">5.</span>
              <span>Share with 3-5 early users and gather feedback</span>
            </li>
          </ol>
        </ExpandableCallout>

        {/* Prerequisites */}
        <PlaybookCard>
          <h3 className="text-xl font-semibold text-white mb-4">Prerequisites</h3>
          <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700/50 mb-4">
            <h4 className="font-semibold text-white mb-3">Required Tools:</h4>
            <ul className="space-y-2 text-gray-300">
              <li className="flex items-start">
                <span className="text-primary-light mr-3">—</span>
                <span>ChatGPT PRO subscription ($20/month)</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-3">—</span>
                <span>Bolt.new or Lovable.com PRO ($20/month)</span>
              </li>
            </ul>
          </div>
          <p className="text-gray-300">
            So you've spotted good use cases. Now it's time to <strong className="text-white">ship something real</strong> — 
            a small, scrappy GPT that solves a problem in your org.
          </p>
        </PlaybookCard>
        <PlaybookCard>
        <p className="text-gray-300">The tools out there are now so good that you can build your first AI agent in under 5 minutes and save yourself 5-15 hours of work every week.</p>
        <p className="text-gray-300">No coding required.</p>
        <PlaybookImage src="https://auth.openorg.fyi/storage/v1/object/public/resources/leverage-ai-for-your-org/AI%20Stack.png" alt="AI Stack" caption="My AI Stack" />
        </PlaybookCard>

        {/* Prompt Engineering 102 */}
        <PlaybookCard>
          <div className="flex items-start mb-4">
            <SparklesIcon className="h-6 w-6 text-primary-light mr-3 mt-1 flex-shrink-0" />
            <h3 className="text-xl font-semibold text-white">Prompt Engineering 102</h3>
          </div>
          
          <div className="bg-primary/10 border border-primary/30 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-white mb-2">Why This Matters</h4>
            <p className="text-gray-300">
              If you're building your first custom GPT agents, the prompt <em>is the product</em>. 
              A strong prompt turns an OK tool into a "holy sh*t that saved me an hour" tool.
            </p>
          </div>

          <p className="text-gray-300 mb-6">
            And the good news? You don't need to code. You need to think clearly, write sharply, and test like a human.
          </p>

          <h4 className="font-semibold text-white mb-4">Core Principles</h4>
          <div className="overflow-x-auto mb-6">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left py-3 text-white font-medium">Principle</th>
                  <th className="text-left py-3 text-white font-medium">What it Means</th>
                  <th className="text-left py-3 text-white font-medium">Why it Matters</th>
                </tr>
              </thead>
              <tbody className="text-gray-300">
                <tr className="border-b border-gray-800">
                  <td className="py-3 font-medium">Context is everything</td>
                  <td className="py-3">Tell the GPT what role it's playing</td>
                  <td className="py-3">Otherwise it'll act like a generic chatbot</td>
                </tr>
                <tr className="border-b border-gray-800">
                  <td className="py-3 font-medium">Clarity beats cleverness</td>
                  <td className="py-3">Write simply and explicitly</td>
                  <td className="py-3">GPTs work best with structured input</td>
                </tr>
                <tr className="border-b border-gray-800">
                  <td className="py-3 font-medium">Chunk the task</td>
                  <td className="py-3">Break big asks into steps</td>
                  <td className="py-3">Prevents vague or lazy responses</td>
                </tr>
                <tr>
                  <td className="py-3 font-medium">Defaults reduce risk</td>
                  <td className="py-3">Pre-fill instructions and examples</td>
                  <td className="py-3">Helps users get value fast</td>
                </tr>
              </tbody>
            </table>
          </div>
        </PlaybookCard>

        {/* Prompt Template */}
        <PlaybookCard>
          <h3 className="text-xl font-semibold text-white mb-4">Prompt Template You Can Reuse</h3>
          <p className="text-gray-300 mb-4">
            Here's a proven template for building a strong GPT agent prompt. You can tweak it for survey synthesis, 
            candidate review, policy generation, etc.
          </p>

          <CopyToNotion title="The Agent Builder Prompt Template">
            <div className="space-y-4">
              <p className="font-bold">You are [role]. Your job is to [task description].</p>
              
              <p>You will receive [input format]. Based on this, do the following:</p>
              
              <ol className="pl-4 space-y-1">
                <li>1. [Step 1: Summarise, categorise, identify X]</li>
                <li>2. [Step 2: Recommend, write, compare, flag issues]</li>
                <li>3. [Step 3: Output the final result in [format] with [tone or constraints]]</li>
              </ol>
              
              <p className="font-bold">Do not guess. Ask clarifying questions if the input is unclear.</p>
            </div>
          </CopyToNotion>
        </PlaybookCard>

        {/* Example Implementation */}
        <PlaybookCard>
          <h3 className="text-xl font-semibold text-white mb-4">Example: Candidate Interview Feedback Synthesiser</h3>
          
          <div className="mb-4">
            <h4 className="font-semibold text-white mb-2">Agent Name: <em>FeedbackSynth GPT</em></h4>
          </div>

          <CopyToNotion title="FeedbackSynth GPT Prompt">
            <div className="space-y-4">
              <p>You are a People Operations assistant. Your job is to help hiring managers summarise interview feedback into a clear, structured, and fair decision summary.</p>
              
              <p>You will receive a list of bullet points, freeform comments, and a scorecard from multiple interviewers.</p>
              
              <p className="font-bold">Your tasks:</p>
              <ol className="pl-4 space-y-1">
                <li>1. Extract themes and surface any discrepancies</li>
                <li>2. Highlight standout strengths or concerns</li>
                <li>3. Write a 3-paragraph summary using neutral, inclusive language</li>
                <li>4. Output it in this format: [Summary], [Strengths], [Risks/Concerns], [Decision Recommendation]</li>
              </ol>
              
              <p className="font-bold">Do not make up information. If a section is blank or contradictory, flag it for review.</p>
            </div>
          </CopyToNotion>

          <div className="mt-6 bg-gray-800/50 rounded-lg p-4 border border-gray-700/50">
            <h4 className="font-semibold text-white mb-3">Why it Works:</h4>
            <ul className="space-y-2 text-gray-300">
              <li className="flex items-start">
                <span className="text-primary-light mr-3">—</span>
                <span><strong className="text-white">Clear role:</strong> tells GPT it's not the decision-maker</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-3">—</span>
                <span><strong className="text-white">Structured output:</strong> prevents waffle</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-3">—</span>
                <span><strong className="text-white">Bias-aware instructions:</strong> builds trust with users</span>
              </li>
            </ul>
          </div>
        </PlaybookCard>

        {/* Example GPTs */}
        <PlaybookCard>
          <h3 className="text-xl font-semibold text-white mb-4">Some People Team GPTs we've built recently for folks</h3>
          <p className="text-gray-300 mb-6">
            Please feel free to bookmark, clone and use these as inspiration for your own creations:
          </p>
          
          <div className="space-y-4">
            <ToolCallout
              label="Sazzle Survey Analyser GPT"
              href="https://www.openverse.fyi/resource/sazzle-the-survey-results-analyser"
              description="Automatically analyzes survey responses and extracts key themes and insights"
              tool="openai"
            />
            
            <ToolCallout
              label="Vi Competency Path Designer GPT"
              href="https://www.openverse.fyi/resource/competency-and-career-framework-designer-gpt-tool"
              description="Helps design competency frameworks and career progression paths"
              tool="openai"
            />
          </div>
        </PlaybookCard>

        {/* Building with UI Tools */}
        <PlaybookCard>
          <h3 className="text-xl font-semibold text-white mb-4">How to Prototype Scrappy Internal Tools</h3>
          <p className="text-gray-300 mb-4">
            Use GPT as your brain. Use Bolt or Lovable to wrap it in a UI. 5 minutes later… you've got a real, usable tool!
          </p>

          <PlaybookImage 
            src="https://auth.openorg.fyi/storage/v1/object/public/resources/leverage-ai-for-your-org/popdash.webp" 
            alt="People Operations Dashboard prototype" 
            caption="People Operations Dashboard prototype built and deployed in 5 minutes" 
          />

          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <ToolCallout
              label="Bolt.new"
              href="https://bolt.new"
              description="AI-powered full-stack development platform for rapid prototyping"
              tool="bolt"
            />
            
            <ToolCallout
              label="Lovable.com"
              href="https://lovable.com"
              description="AI code generator for building web applications quickly"
              tool="lovable"
            />
          </div>

          <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700/50 mb-4">
            <h4 className="font-semibold text-white mb-3">What You Can Build in Minutes:</h4>
            <ul className="space-y-2 text-gray-300">
              <li className="flex items-start">
                <span className="text-primary-light mr-3">—</span>
                <span>Simple forms that feed data to your GPT</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-3">—</span>
                <span>Dashboards that display GPT outputs in a clean format</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-3">—</span>
                <span>File upload interfaces for document processing</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-3">—</span>
                <span>Multi-step workflows with progress indicators</span>
              </li>
            </ul>
          </div>

          <p className="text-gray-300">
            No code. Just a good prompt, a few dropdowns, and a way to collect inputs.
          </p>
        </PlaybookCard>

        {/* Video Tutorial */}
        <PlaybookCard>
          <h3 className="text-xl font-semibold text-white mb-4">Want to watch me do it?</h3>
          <p className="text-gray-300 mb-4">
            Check out a live walk-through of me building a custom GPT agent from scratch and a glittering gradient people ops dashboard!
          </p>
          <VideoEmbed 
            src="https://www.youtube.com/embed/vuBFgOBTi4k"
            title="Building a custom GPT agent from scratch"
            caption="Live demonstration of John creating a custom GPT agent and POPs dashboard from scratch in minutes"
          />
        </PlaybookCard>

        {/* Step-by-Step Process */}
        <PlaybookCard>
          <h3 className="text-xl font-semibold text-white mb-4">TL;DR – How to Build Your First Agent</h3>
          <ol className="space-y-4 text-gray-300">
            <li className="flex items-start">
              <span className="text-primary-light font-bold mr-3">1.</span>
              <div>
                <p className="font-semibold text-white">Pick a painful task</p>
                <p className="text-sm">Choose something that's high-friction and low-fun from your Stage 1 list</p>
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light font-bold mr-3">2.</span>
              <div>
                <p className="font-semibold text-white">Write a strong prompt</p>
                <p className="text-sm">Use the Agent Builder template above</p>
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light font-bold mr-3">3.</span>
              <div>
                <p className="font-semibold text-white">Test on real messy input</p>
                <p className="text-sm">Don't just use your perfect example—try actual chaotic data</p>
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light font-bold mr-3">4.</span>
              <div>
                <p className="font-semibold text-white">Wrap it in a shareable tool</p>
                <p className="text-sm">Use Bolt or Lovable to create a simple interface</p>
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light font-bold mr-3">5.</span>
              <div>
                <p className="font-semibold text-white">Ship it, share it, and improve</p>
                <p className="text-sm">Get it in front of real users and iterate based on feedback</p>
              </div>
            </li>
          </ol>
        </PlaybookCard>

        {/* Testing Framework */}
        <PlaybookCard>
          <h3 className="text-xl font-semibold text-white mb-4">Testing Your GPT Agent</h3>
          <p className="text-gray-300 mb-4">
            Before you share widely, test your agent with these scenarios:
          </p>
          
          <div className="space-y-4">
            <ExpandableCallout 
              title="Edge Case Testing Checklist"
              type="tip"
              defaultOpen={true}
            >
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start">
                  <span className="text-primary-light mr-3">—</span>
                  <span><strong className="text-white">Incomplete input:</strong> What happens when someone gives partial information?</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-3">—</span>
                  <span><strong className="text-white">Messy formatting:</strong> Test with real copy-pasted data, not clean examples</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-3">—</span>
                  <span><strong className="text-white">Contradictory information:</strong> How does it handle conflicting inputs?</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-3">—</span>
                  <span><strong className="text-white">Unexpected requests:</strong> What if users ask it to do something outside its scope?</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-3">—</span>
                  <span><strong className="text-white">Volume stress test:</strong> Does it maintain quality with larger inputs?</span>
                </li>
              </ul>
            </ExpandableCallout>
          </div>
        </PlaybookCard>

        {/* Common Pitfalls */}
        <ExpandableCallout 
          title="Common Pitfalls & How to Avoid Them"
          type="warning"
          defaultOpen={true}
        >
          <div className="space-y-4">
            <div>
              <h4 className="font-semibold text-white mb-2">Pitfall: Trying to build the "perfect" GPT</h4>
              <p className="text-gray-300"><strong>Solution:</strong> Start with 80% functionality. Ship it. Improve based on real usage.</p>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-2">Pitfall: Making the prompt too complex</h4>
              <p className="text-gray-300"><strong>Solution:</strong> Keep it simple and focused. One clear job to be done.</p>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-2">Pitfall: Not testing with real data</h4>
              <p className="text-gray-300"><strong>Solution:</strong> Use actual messy inputs from your team, not sanitized examples.</p>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-2">Pitfall: Building in isolation</h4>
              <p className="text-gray-300"><strong>Solution:</strong> Get early feedback from 2-3 potential users before investing more time.</p>
            </div>
          </div>
        </ExpandableCallout>

        {/* Outcome */}
        <div className="bg-primary/10 border border-primary/30 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Stage 2 Outcome</h3>
          <p className="text-gray-300 mb-4">
            By the end of this stage, you should have:
          </p>
          <ul className="space-y-2 text-gray-300 mb-4">
            <li className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <span>At least one working GPT agent that solves a real problem</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <span>A simple UI wrapper that makes it easy for others to use</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <span>Feedback from 3-5 early users on functionality and usefulness</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <span>Confidence in the prompt engineering process for future tools</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <span>A concrete example to share in your AI demos and discussions</span>
            </li>
          </ul>
          <p className="text-gray-300">
            <strong>Next:</strong> Move to <a href="/playbooks/leverage-ai-for-you-and-your-org/stage-3" className="text-primary hover:text-primary-light">Stage 3</a> to scale this across your organization.
          </p>
        </div>
      </div>
    </PlaybookSection>
  );
};

export default Stage2; 